import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { NdaGate } from '@features/legal/components/NdaGate'
import { PendingDeletionPrompt } from '@features/profile/components/settings/PendingDeletionPrompt'
import { ReactNode } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: string[]
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0) {
    // Debug logging
    console.log('[ProtectedRoute] Required roles:', requiredRoles)
    console.log('[ProtectedRoute] User roles:', user?.roles)
    console.log('[ProtectedRoute] User object:', user)

    const hasRequiredRole = requiredRoles.some(role => user?.roles?.includes(role))
    console.log('[ProtectedRoute] Has required role:', hasRequiredRole)

    if (!hasRequiredRole) {
      console.warn('[ProtectedRoute] Access denied - missing required role')
      return <Navigate to="/unauthorized" replace />
    }
  }

  // The NDA gate sits here, at the single point every authenticated route already passes through,
  // and deliberately below the auth check: the login and registration flows never render this, so
  // gating it cannot lock anybody out of signing in. Public routes (share codes, password reset)
  // are outside it too, which matches the server — it does not gate anonymous requests either.
  //
  // The pending-deletion prompt rides the same point: every signed-in page, never the sign-in flow.
  return (
    <NdaGate>
      {children}
      <PendingDeletionPrompt />
    </NdaGate>
  )
}
